import React, { useEffect, useMemo, useRef, useState } from "react";
import { Form, Row, Col, DatePicker, message, Typography, Table, Select } from 'antd';
import { useLocation } from 'react-router-dom';
import { getRangeOptions, getPaginationDetails, getDefaultPageSize, parseSalesReportLink } from './reports.module';
import { fetchTripsByBookingDate, fetchTripsByTravelDate, fetchSalesSheet } from './../../actions/reports.action';
import { getAPIDateFormat, convertDateStringToDate, getLocalDate, getLocalDateAndTime } from './../../utils/date.utils';
import { ScheduleSelect } from './../../components/search-components/schedule-select/schedule-select.component';
import { isPvtLtdCompany, isValid, isValidArray, isValidBool, isValidString } from "../../utils/utilities";
import { addFloatingPoints as AFP, subtractFloatingPoints as SFP, roundNumber } from './../../utils/math.utils';
import { Printer } from './../../components/custom-components/custom-component';
import { getVechicles } from './../../components/edit-vechicle-component/edit-vechicle.module';
import * as XLSX from 'xlsx';
import './sales-ticket.report.scss';

const { Text } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

const allowedMaxDaysRange = 31;
const xs = 24, sm = 24, md = 12, lg = 12, xl = 12, xxl = 12;

// ============================================================
// Formatters
// ============================================================
const fmtINR = (n) => {
    if (n == null || isNaN(n)) return '—';
    const num = Number(n);
    const hasDecimal = num % 1 !== 0;
    return '₹' + num.toLocaleString('en-IN', hasDecimal ? { minimumFractionDigits: 2, maximumFractionDigits: 2 } : {});
};
const fmtINRpaise = (n) => {
    if (n == null || isNaN(n)) return '—';
    return '₹' + Number(n).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};
// Collection mode column - shown only for operators that have UPI booking
// enabled. Operators without the ENABLE_UPI_BOOKING config get no mode column
// and no mode filter at all. A booking with a payment gateway order number made
// by any agent other than the website agent was collected over UPI; everything
// else is cash.
const MODE_UPI = 'UPI';
const MODE_CASH = 'Cash';
const WEB_AGENT_NAMES = ['web', 'website'];
const isModeEnabled = () => isValid(global.travelsAppConfig) && global.travelsAppConfig['ENABLE_UPI_BOOKING'] == 1;
const isWebAgent = (name) => WEB_AGENT_NAMES.includes(String(name || '').toLowerCase());
const getTicketMode = (record) => {
    if (!isValid(record)) return MODE_CASH;
    return isValidString(record.order_no) && !isWebAgent(record.name) ? MODE_UPI : MODE_CASH;
};

const getChannelClass = (name) => {
    if (!name) return 'ch-web';
    const n = String(name).toLowerCase();
    if (n.includes('redbus')) return 'ch-redbus';
    if (n.includes('abhibus')) return 'ch-abhibus';
    if (n.includes('paytm')) return 'ch-paytm';
    if (n.includes('website')) return 'ch-website';
    return 'ch-web';
};

// ============================================================
// Inline icons (kept simple — match the mockup)
// ============================================================
const Icon = {
    search: <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6"><circle cx="7" cy="7" r="5"/><path d="m11 11 3 3"/></svg>,
    calendar: <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6"><rect x="2" y="3" width="12" height="11" rx="1.5"/><path d="M5 1v3M11 1v3M2 6h12"/></svg>,
    download: <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6"><path d="M8 2v9m-3-3 3 3 3-3M2 13h12"/></svg>,
    print: <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6"><path d="M4 6V2h8v4M4 12H2V7h12v5h-2M4 9h8v6H4z"/></svg>,
    caretRight: <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.7"><path d="m6 4 4 4-4 4"/></svg>,
    caretDown: <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.7"><path d="m4 6 4 4 4-4"/></svg>,
    run: <svg viewBox="0 0 16 16" fill="currentColor"><path d="M5 3l8 5-8 5z"/></svg>,
};

// ============================================================
// Preserved helpers (untouched logic — drive Excel export + summary aggregation)
// ============================================================
const filterValidation = (filters, ticketAgentName) => {
    let { isSelectAllAgents, agentName } = filters;
    if (isSelectAllAgents) return true;
    if (isValidArray(agentName) && agentName.length > 0) return agentName.includes(ticketAgentName);
    return false;
};

const getAgentFilterOptions = (tickets) => {
    if (!isValidArray(tickets) || tickets.length === 0) return [];
    const agentNames = new Set();
    tickets.forEach(ticket => { if (isValid(ticket.name)) agentNames.add(ticket.name); });
    return Array.from(agentNames).sort().map(name => ({ text: name, value: name }));
};

const getSubAgentFilterOptions = (tickets) => {
    if (!isValidArray(tickets) || tickets.length === 0) return [];
    const names = new Set();
    tickets.forEach(t => { if (isValid(t.sub_agent_name)) names.add(t.sub_agent_name); });
    return Array.from(names).sort().map(name => ({ text: name, value: name }));
};

// 1-line tweak: prefer column.exportRender when present so display `render` can return JSX.
const generateJsonToExport = (filters, confirmedTickets, cancelledTickets, confirmColumns, cancelColumns) => {
    let reportsConfirmedXmlData = [];
    let reportsCancelledXmlData = [];

    const renderForExport = (column) => column.exportRender || column.render;
    const titleForExport = (column) => isValid(column.excelTitle) ? column.excelTitle : (typeof column.title === 'string' ? column.title : column.key);

    const confirmHeaders = confirmColumns.map(titleForExport);
    const cancelHeaders = cancelColumns.map(titleForExport);

    if (isValidArray(confirmedTickets) && confirmedTickets.length > 0) {
        confirmedTickets.forEach((element, index) => {
            if (filterValidation(filters, element.name)) {
                let dataColumn = [];
                confirmColumns.forEach(column => {
                    const r = renderForExport(column);
                    dataColumn.push(r ? r(element[column.dataIndex], element, index) : '');
                });
                reportsConfirmedXmlData.push(dataColumn);
            }
        });
        const confirmedSummary = calculateSummary(confirmedTickets, confirmColumns);
        if (confirmedSummary) {
            let summaryRow = [];
            confirmColumns.forEach((column, index) => {
                if (index === 0) summaryRow.push('Total');
                else if (column.dataIndex && confirmedSummary[column.dataIndex] !== undefined) summaryRow.push(roundNumber(confirmedSummary[column.dataIndex]));
                else if (column.key && confirmedSummary[column.key] !== undefined) summaryRow.push(roundNumber(confirmedSummary[column.key]));
                else summaryRow.push('');
            });
            reportsConfirmedXmlData.push(summaryRow);
        }
    }

    if (isValidArray(cancelledTickets) && cancelledTickets.length > 0) {
        cancelledTickets.forEach((element, index) => {
            if (filterValidation(filters, element.name)) {
                let dataColumn = [];
                cancelColumns.forEach(column => {
                    const r = renderForExport(column);
                    dataColumn.push(r ? r(element[column.dataIndex], element, index) : '');
                });
                reportsCancelledXmlData.push(dataColumn);
            }
        });
        const cancelledSummary = calculateSummary(cancelledTickets, cancelColumns);
        if (cancelledSummary) {
            let summaryRow = [];
            cancelColumns.forEach((column, index) => {
                if (index === 0) summaryRow.push('Total');
                else if (column.dataIndex && cancelledSummary[column.dataIndex] !== undefined) summaryRow.push(roundNumber(cancelledSummary[column.dataIndex]));
                else if (column.key && cancelledSummary[column.key] !== undefined) summaryRow.push(roundNumber(cancelledSummary[column.key]));
                else summaryRow.push('');
            });
            reportsCancelledXmlData.push(summaryRow);
        }
    }

    let agentName = filters.isSelectAllAgents ? 'all' : (isValidArray(filters.agentName) ? filters.agentName.join(', ') : '');
    let dateType = filters.isBookingDate ? "Booking" : "Travel";
    let resultType = filters.resultType || 'detailed';
    let generatedTime = getLocalDateAndTime(new Date());
    var worksheetArray = [];
    worksheetArray.push([]);
    worksheetArray.push([]);
    worksheetArray.push(["Report", "Agents", "From Date", "To Date", "Type of date", "Generated day/time", "Type of report"]);
    worksheetArray.push(["Sales report", agentName, filters.fromDate, filters.toDate, dateType, generatedTime, resultType]);
    worksheetArray.push([]);
    worksheetArray.push(confirmHeaders);
    worksheetArray.push(...reportsConfirmedXmlData);
    worksheetArray.push([]);
    worksheetArray.push([]);
    worksheetArray.push(["Cancelled Tickets"]);
    worksheetArray.push(cancelHeaders);
    worksheetArray.push(...reportsCancelledXmlData);

    var worksheet = XLSX.utils.aoa_to_sheet(worksheetArray);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Sales Report');
    XLSX.writeFile(workbook, 'sales-report.xlsx');
};

const getTripIds = (trips, isSelectAll, scheduleId) => {
    let tripIds = [];
    if (isValidArray(trips) && trips.length > 0) {
        trips.forEach(element => {
            if (isSelectAll) tripIds.push(element.id);
            else if (scheduleId === element.schedule_id) tripIds.push(element.id);
        });
    }
    return tripIds;
};

const getTicketsByAgentName = (tickets, agentName, isSelectAllAgents, vehicleFilter) => {
    if (!isValidArray(tickets)) return [];
    const hasAgentFilter = isValidArray(agentName) && agentName.length > 0;
    // vehicleFilter accepts either an array of vehicle ids or a single id (legacy callers).
    const vehicleArray = Array.isArray(vehicleFilter)
        ? vehicleFilter
        : (isValid(vehicleFilter) ? [vehicleFilter] : []);
    const hasVehicleFilter = vehicleArray.length > 0;
    const matchesVehicle = (vid) => hasVehicleFilter && vehicleArray.some(v => v == vid);
    const allfilterApplied = !isSelectAllAgents && hasAgentFilter && hasVehicleFilter;

    if (isSelectAllAgents && !hasVehicleFilter) return tickets;

    const formatedTickets = [];
    if (tickets.length > 0 && (hasAgentFilter || hasVehicleFilter)) {
        tickets.forEach(element => {
            if (allfilterApplied) {
                if (hasAgentFilter && agentName.includes(element.name) && matchesVehicle(element.vehicle_id)) formatedTickets.push(element);
            } else {
                if (!isSelectAllAgents && hasAgentFilter && agentName.includes(element.name)) formatedTickets.push(element);
                if (hasVehicleFilter && matchesVehicle(element.vehicle_id)) formatedTickets.push(element);
            }
        });
    }
    return formatedTickets;
};

// Cross operator bookings carry the booking agent's operator name; own bookings
// carry the operator's own name. Filter is a plain include on that field.
const getTicketsByOperator = (tickets, operatorNames, isSelectAllOperators) => {
    if (!isValidArray(tickets)) return [];
    if (isSelectAllOperators || !isValidArray(operatorNames) || operatorNames.length === 0) return tickets;
    return tickets.filter(ticket => operatorNames.includes(ticket.agent_operator_name));
};

// Mode filter - plain include on the mode stamped by the backend.
const getTicketsByMode = (tickets, modes, isSelectAllModes) => {
    if (!isValidArray(tickets)) return [];
    if (isSelectAllModes || !isValidArray(modes) || modes.length === 0) return tickets;
    return tickets.filter(ticket => modes.includes(getTicketMode(ticket)));
};

const calculateSummary = (data, columns) => {
    if (!data || data.length === 0) return null;
    const summary = {};
    columns.forEach(column => {
        if (column.dataIndex) {
            if (typeof data[0][column.dataIndex] == 'number') {
                summary[column.dataIndex] = data.reduce((acc, curr) => AFP(acc, curr[column.dataIndex] || 0), 0);
            }
        } else if (column.key) {
            switch (column.key) {
                case 'gst':
                    summary[column.key] = data.reduce((acc, curr) => AFP(acc, AFP(curr.cgst || 0, curr.sgst || 0)), 0); break;
                case 'net_commision':
                    summary[column.key] = data.reduce((acc, curr) => AFP(acc, SFP(curr.commission || 0, curr.tds || 0)), 0); break;
                case 'net_amount':
                    summary[column.key] = data.reduce((acc, curr) => {
                        let gstAmount = (isValid(curr.privilege) && curr.privilege == "OTA") ? (curr.cgst || 0) + (curr.sgst || 0) : 0;
                        return AFP(acc, curr.amountPaid - (curr.commission - curr.tds) - gstAmount);
                    }, 0); break;
                case 'refund_amount':
                    summary[column.key] = data.reduce((acc, curr) => AFP(acc, SFP(curr.amountPaid || 0, curr.cancellation_penalty || 0)), 0); break;
                default: break;
            }
        }
    });
    return summary;
};

// ============================================================
// A–I summary rows for the new SummaryCard (lifted from legacy RenderReport)
// ============================================================
const buildSummaryRows = (filteredConfirmed, filteredCancelled) => {
    const sumFare = (rows) => isValidArray(rows) ? rows.reduce((a, t) => AFP(a, t.fare || 0), 0) : 0;
    const sumNonOtaGst = (rows) => isValidArray(rows) ? rows.reduce((a, t) => {
        if (!isValid(t.privilege) || t.privilege !== "OTA" || isPvtLtdCompany()) {
            return AFP(a, AFP(t.cgst || 0, t.sgst || 0));
        }
        return a;
    }, 0) : 0;
    const sumDirectDiscount = (rows) => isValidArray(rows) ? rows.reduce((a, t) => AFP(a, t.direct_discount || 0), 0) : 0;
    const sumField = (rows, field) => isValidArray(rows) ? rows.reduce((a, t) => AFP(a, t[field] || 0), 0) : 0;
    const sumNetCommission = (rows) => isValidArray(rows) ? rows.reduce((a, t) => AFP(a, SFP(t.commission || 0, t.tds || 0)), 0) : 0;

    // A: Ticket fare
    const cFare = sumFare(filteredConfirmed);
    const xFare = sumFare(filteredCancelled);
    // B: GST (Non-OTA)
    const cGst = sumNonOtaGst(filteredConfirmed);
    const xGst = sumNonOtaGst(filteredCancelled);
    // C: Total amount = A + B
    const cTotal = AFP(cFare, cGst);
    const xTotal = AFP(xFare, xGst);
    // D: Discount
    const cDisc = sumDirectDiscount(filteredConfirmed);
    const xDisc = sumDirectDiscount(filteredCancelled);
    // E: Total amount paid (C − D)
    const cPaid = SFP(cTotal, cDisc);
    const xPaid = SFP(xTotal, xDisc);
    // F: Commission
    const cComm = sumField(filteredConfirmed, 'commission');
    const xComm = sumField(filteredCancelled, 'commission');
    // G: TDS
    const cTds = sumField(filteredConfirmed, 'tds');
    const xTds = sumField(filteredCancelled, 'tds');
    // H: Net commission (F − G)
    const cNetComm = sumNetCommission(filteredConfirmed);
    const xNetComm = sumNetCommission(filteredCancelled);
    // I: Cancellation penalty share for operator (penalty_commission on cancelled)
    const xPenaltyComm = sumField(filteredCancelled, 'penalty_commission');

    const revenue = [
        { code: 'A', metric: 'Ticket Fare', confirmed: cFare, cancelled: xFare, relevant: SFP(cFare, xFare) },
        { code: 'B', metric: isPvtLtdCompany() ? 'GST' : 'GST (Non-OTA)', confirmed: cGst, cancelled: xGst, relevant: SFP(cGst, xGst) },
        { code: 'C', metric: 'Total Amount (A + B)', confirmed: cTotal, cancelled: xTotal, relevant: SFP(cTotal, xTotal), subtotal: true },
        { code: 'D', metric: 'Discount given to customers', confirmed: cDisc, cancelled: xDisc, relevant: SFP(cDisc, xDisc) },
        { code: 'E', metric: 'Total amount paid by customers (C − D)', confirmed: cPaid, cancelled: xPaid, relevant: SFP(cPaid, xPaid), subtotal: true },
    ];
    const commissions = [
        { code: 'F', metric: 'Commission for agents', confirmed: cComm, cancelled: xComm, relevant: SFP(cComm, xComm) },
        { code: 'G', metric: 'TDS deducted from commission', confirmed: cTds, cancelled: xTds, relevant: SFP(cTds, xTds) },
        { code: 'H', metric: 'Net Commission (F − G)', confirmed: cNetComm, cancelled: xNetComm, relevant: SFP(cNetComm, xNetComm), subtotal: true },
    ];
    const operator = [
        { code: 'I', metric: 'Cancellation penalty share for operator', confirmed: null, cancelled: xPenaltyComm, relevant: xPenaltyComm },
    ];

    const fromCustomers = SFP(cPaid, xPaid);                   // E (relevant)
    const netCommissionRelevant = SFP(cNetComm, xNetComm);     // H (relevant)
    const netCash = AFP(SFP(fromCustomers, netCommissionRelevant), xPenaltyComm);

    return { revenue, commissions, operator, netCash, fromCustomers, netCommissionRelevant };
};

// ============================================================
// Sub-components
// ============================================================
function PageHeader({ onExport, canExport, printerNode, onPrint }) {
    return (
        <div className="pageheader">
            <div>
                <div className="breadcrumb">
                    <span>Reports</span>{Icon.caretRight}<span style={{ color: 'var(--ink-2)' }}>Sales report</span>
                </div>
                <h1>Sales Report</h1>
            </div>
            <div className="pageheader-actions">
                {printerNode}
                <button type="button" className="btn primary" disabled={!canExport} onClick={onPrint}>
                    {Icon.print}Print
                </button>
                <button type="button" className="btn primary" disabled={!canExport} onClick={onExport}>
                    {Icon.download}Export
                </button>
            </div>
        </div>
    );
}

function FilterCard({
    form, schedules,
    dateType, setDateType,
    onSubmit, onReset, onValuesChange, running,
}) {
    const handleSubmit = (values) => onSubmit({ ...values, isBooking: dateType });

    return (
        <div className="card">
            <Form
                form={form}
                onFinish={handleSubmit}
                onValuesChange={(changed, all) => onValuesChange(changed, { ...all, isBooking: dateType })}
            >
                <div className="filter-grid">
                    <div className="field" style={{ gridColumn: '1 / span 2', gridRow: 1 }}>
                        <div className="field-label">Date type</div>
                        <div className="segmented">
                            <button type="button"
                                className={dateType === true ? 'active' : ''}
                                onClick={() => setDateType(true)}>Booking</button>
                            <button type="button"
                                className={dateType === false ? 'active' : ''}
                                onClick={() => setDateType(false)}>Travel</button>
                        </div>
                    </div>

                    <div className="field field-rangepicker-wrap" style={{ gridColumn: '3 / span 4', gridRow: 1 }}>
                        <div className="field-label">Date range</div>
                        <Form.Item name="dateRange" rules={[
                            { required: true, message: 'Please select a date range' },
                            () => ({
                                validator(_, value) {
                                    if (!value || (value[1].diff(value[0], "days") <= allowedMaxDaysRange)) return Promise.resolve();
                                    return Promise.reject(new Error(`Please select a date range within ${allowedMaxDaysRange} days`));
                                },
                            }),
                        ]}>
                            <RangePicker ranges={getRangeOptions(allowedMaxDaysRange)} format="DD MMM YYYY" />
                        </Form.Item>
                    </div>

                    <div className="field field-select-wrap" style={{ gridColumn: '7 / span 6', gridRow: 1 }}>
                        <div className="field-label">Schedule</div>
                        <ScheduleSelect
                            schedules={schedules || []}
                            optionKey="id"
                            showLabel={false}
                            isManditory={false}
                            fieldName="returnId"
                        />
                    </div>

                    <div className="filter-actions" style={{ gridColumn: '1 / -1', gridRow: 2, display: 'flex', gap: 8, justifyContent: 'flex-start', alignItems: 'flex-end' }}>
                        <button type="button" className="btn" onClick={onReset} disabled={running}>Reset</button>
                        <button type="submit" className="btn primary" disabled={running}>
                            {Icon.run}{running ? 'Running…' : 'Run report'}
                        </button>
                    </div>
                </div>
            </Form>
        </div>
    );
}

function RefineBar({
    agentOptions, selectedAgents, isSelectAllAgents, onAgentChange,
    vehicleOptions, selectedVehicles, isSelectAllVehicles, onVehicleChange,
    operatorOptions, selectedOperators, isSelectAllOperators, onOperatorChange,
    selectedModes, isSelectAllModes, onModeChange,
}) {
    const allAgents = useMemo(() => {
        if (!isValidArray(agentOptions)) return [];
        return [...new Set(agentOptions.map(a => (a && a.name) || a).filter(Boolean))].sort();
    }, [agentOptions]);

    const allVehicleIds = useMemo(
        () => isValidArray(vehicleOptions) ? vehicleOptions.map(v => v.id) : [],
        [vehicleOptions]
    );

    const handleAgentChange = (vals) => {
        const selected = vals || [];
        const selectAll = selected.length === 0 || (allAgents.length > 0 && selected.length >= allAgents.length);
        onAgentChange({
            agentName: selectAll ? allAgents : selected,
            isSelectAllAgents: selectAll,
        });
    };

    const handleVehicleChange = (vals) => {
        const selected = vals || [];
        const selectAll = selected.length === 0 || (allVehicleIds.length > 0 && selected.length >= allVehicleIds.length);
        onVehicleChange({
            vehicleNumbers: selectAll ? allVehicleIds : selected,
            isSelectAllVehicles: selectAll,
        });
    };

    // Operator filter is shown only when the result has bookings from multiple operators
    const allOperators = isValidArray(operatorOptions) ? operatorOptions : [];
    const showOperatorFilter = allOperators.length > 1;

    const handleOperatorChange = (vals) => {
        const selected = vals || [];
        const selectAll = selected.length === 0 || (allOperators.length > 0 && selected.length >= allOperators.length);
        onOperatorChange({
            operatorNames: selectAll ? allOperators : selected,
            isSelectAllOperators: selectAll,
        });
    };

    // Mode filter is shown only for operators with UPI booking enabled
    const showModeFilter = isModeEnabled();
    const allModes = [MODE_UPI, MODE_CASH];

    const handleModeChange = (vals) => {
        const selected = vals || [];
        const selectAll = selected.length === 0 || selected.length >= allModes.length;
        onModeChange({
            modes: selectAll ? allModes : selected,
            isSelectAllModes: selectAll,
        });
    };

    const agentValue = isSelectAllAgents ? [] : (selectedAgents || []);
    const vehicleValue = isSelectAllVehicles ? [] : (selectedVehicles || []);
    const operatorValue = isSelectAllOperators ? [] : (selectedOperators || []);
    const hasActiveAgentFilter = !isSelectAllAgents;
    const hasActiveVehicleFilter = !isSelectAllVehicles;
    const hasActiveOperatorFilter = !isSelectAllOperators;
    const modeValue = isSelectAllModes ? [] : (selectedModes || []);
    const hasActiveModeFilter = showModeFilter && !isSelectAllModes;

    return (
        <div className="refine-bar">
            <div className="field field-select-wrap" style={{ flex: 1, minWidth: 0 }}>
                <div className="field-label">Filter by agent</div>
                <Select
                    mode="multiple"
                    allowClear
                    showSearch
                    placeholder={allAgents.length === 0 ? 'No agents in result' : 'All agents'}
                    value={agentValue}
                    onChange={handleAgentChange}
                    maxTagCount={3}
                    maxTagPlaceholder={(omitted) => `+${omitted.length} more`}
                    optionFilterProp="children"
                    filterOption={(input, option) => String(option.children || '').toLowerCase().indexOf(input.toLowerCase()) >= 0}
                    disabled={allAgents.length === 0}
                    style={{ width: '100%' }}
                >
                    {allAgents.map(name => <Option key={name} value={name}>{name}</Option>)}
                </Select>
            </div>
            <div className="field field-select-wrap" style={{ flex: 1, minWidth: 0 }}>
                <div className="field-label">Filter by vehicle</div>
                <Select
                    mode="multiple"
                    allowClear
                    showSearch
                    placeholder={allVehicleIds.length === 0 ? 'No vehicles in result' : 'All vehicles'}
                    value={vehicleValue}
                    onChange={handleVehicleChange}
                    maxTagCount={3}
                    maxTagPlaceholder={(omitted) => `+${omitted.length} more`}
                    optionFilterProp="label"
                    filterOption={(input, option) => String(option.label || '').toLowerCase().indexOf(input.toLowerCase()) >= 0}
                    disabled={allVehicleIds.length === 0}
                    style={{ width: '100%' }}
                >
                    {(vehicleOptions || []).map(v => (
                        <Option key={v.id} value={v.id} label={v.label}>{v.label}</Option>
                    ))}
                </Select>
            </div>
            {showOperatorFilter && (
                <div className="field field-select-wrap" style={{ flex: 1, minWidth: 0 }}>
                    <div className="field-label">Filter by operator</div>
                    <Select
                        mode="multiple"
                        allowClear
                        showSearch
                        placeholder="All operators"
                        value={operatorValue}
                        onChange={handleOperatorChange}
                        maxTagCount={3}
                        maxTagPlaceholder={(omitted) => `+${omitted.length} more`}
                        optionFilterProp="children"
                        filterOption={(input, option) => String(option.children || '').toLowerCase().indexOf(input.toLowerCase()) >= 0}
                        style={{ width: '100%' }}
                    >
                        {allOperators.map(name => <Option key={name} value={name}>{name}</Option>)}
                    </Select>
                </div>
            )}
            {showModeFilter && (
                <div className="field field-select-wrap" style={{ flex: 1, minWidth: 0 }}>
                    <div className="field-label">Filter by Payment mode</div>
                    <Select
                        mode="multiple"
                        allowClear
                        placeholder="All modes"
                        value={modeValue}
                        onChange={handleModeChange}
                        style={{ width: '100%' }}
                    >
                        {allModes.map(m => <Option key={m} value={m}>{m}</Option>)}
                    </Select>
                </div>
            )}
            {(hasActiveAgentFilter || hasActiveVehicleFilter || hasActiveOperatorFilter || hasActiveModeFilter) && (
                <button type="button" className="btn" onClick={() => {
                    if (hasActiveAgentFilter) handleAgentChange([]);
                    if (hasActiveVehicleFilter) handleVehicleChange([]);
                    if (hasActiveOperatorFilter) handleOperatorChange([]);
                    if (hasActiveModeFilter) handleModeChange([]);
                }}>Clear</button>
            )}
        </div>
    );
}

function FilterSummaryChips({ filters, generatedAt, vehicleNumberById, scheduleLabel, hasReport }) {
    if (!hasReport) return null;
    const dateLabel = filters.fromDate && filters.toDate ? `${filters.fromDate} – ${filters.toDate}` : '—';
    const agentLabel = filters.isSelectAllAgents ? 'All' :
        (isValidArray(filters.agentName) ? (filters.agentName.length <= 2 ? filters.agentName.join(', ') : `${filters.agentName.length} selected`) : 'All');
    const vehicleLabel = filters.isSelectAllVehicles ? 'All'
        : (isValidArray(filters.vehicleNumbers) && filters.vehicleNumbers.length > 0
            ? (filters.vehicleNumbers.length <= 2
                ? filters.vehicleNumbers.map(id => vehicleNumberById[id] || id).join(', ')
                : `${filters.vehicleNumbers.length} selected`)
            : 'All');
    return (
        <div className="filter-summary">
            <span className="chip"><span className="label">{filters.isBookingDate ? 'Booking' : 'Travel'}</span><strong>{dateLabel}</strong></span>
            <span className="chip"><span className="label">Schedule</span><strong>{scheduleLabel || 'All'}</strong></span>
            <span className="chip"><span className="label">Agent</span><strong>{agentLabel}</strong></span>
            <span className="chip"><span className="label">Vehicle</span><strong>{vehicleLabel}</strong></span>
            {!filters.isSelectAllOperators && isValidArray(filters.operatorNames) &&
                <span className="chip"><span className="label">Operator</span><strong>{filters.operatorNames.length <= 2 ? filters.operatorNames.join(', ') : `${filters.operatorNames.length} selected`}</strong></span>
            }
            {isModeEnabled() && !filters.isSelectAllModes && isValidArray(filters.modes) &&
                <span className="chip"><span className="label">Mode</span><strong>{filters.modes.join(', ')}</strong></span>
            }
            {generatedAt && <span className="gen">Generated {generatedAt}</span>}
        </div>
    );
}

function HeroMetric({ netCash, fromCustomers, netCommission, confirmedCount, cancelledCount }) {
    return (
        <div className="hero-metric">
            <div>
                <div className="l">Net Cash to Operator</div>
                <div className="v">{fmtINRpaise(netCash)}</div>
                <div className="hero-counts">
                    <span className="hero-count confirmed">
                        <strong>{Number(confirmedCount || 0).toLocaleString('en-IN')}</strong> confirmed
                    </span>
                    <span className="hero-count cancelled">
                        <strong>{Number(cancelledCount || 0).toLocaleString('en-IN')}</strong> cancelled
                    </span>
                </div>
                <div className="formula">Total amount paid by customers (E) − Net Commission (H) + Cancellation penalty share (I)</div>
            </div>
            <div className="right">
                <div className="pair">
                    <div className="stat">
                        <div className="l">From customers</div>
                        <div className="v">{fmtINRpaise(fromCustomers)}</div>
                        <div className="sub">Net of discounts</div>
                    </div>
                    <div className="stat">
                        <div className="l">Less: Net commission</div>
                        <div className="v">{fmtINRpaise(netCommission)}</div>
                        <div className="sub">After TDS</div>
                    </div>
                </div>
            </div>
        </div>
    );
}

function SummaryTable({ rows }) {
    return (
        <div className="summary-table">
            <div className="h">Metric</div>
            <div className="h right">Confirmed</div>
            <div className="h right">Cancelled</div>
            <div className="h right">Relevant</div>
            {rows.map(r => (
                <div key={r.code} className={`row ${r.subtotal ? 'subtotal' : ''}`}>
                    <div className="cell metric"><span className="code">{r.code}.</span>{r.metric}</div>
                    <div className="cell value">{fmtINR(r.confirmed)}</div>
                    <div className="cell value">{fmtINR(r.cancelled)}</div>
                    <div className="cell value">{fmtINR(r.relevant)}</div>
                </div>
            ))}
        </div>
    );
}

function SummaryCard({ filteredConfirmed, filteredCancelled, confirmedCount, cancelledCount }) {
    const sum = useMemo(
        () => buildSummaryRows(filteredConfirmed, filteredCancelled),
        [filteredConfirmed, filteredCancelled]
    );
    return (
        <div className="card">
            <div className="card-head">
                <div className="card-title"><span className="dot"></span>Operator Revenue Summary</div>
            </div>
            <HeroMetric
                netCash={sum.netCash}
                fromCustomers={sum.fromCustomers}
                netCommission={sum.netCommissionRelevant}
                confirmedCount={confirmedCount}
                cancelledCount={cancelledCount}
            />
            <div className="summary-section">
                <div className="summary-section-title">Revenue Ledger</div>
                <SummaryTable rows={sum.revenue} />
            </div>
            <div className="summary-section">
                <div className="summary-section-title">Commissions</div>
                <SummaryTable rows={sum.commissions} />
            </div>
            <div className="summary-section">
                <div className="summary-section-title">Operator Income (Penalties)</div>
                <SummaryTable rows={sum.operator} />
            </div>
        </div>
    );
}

function TxDetailGrid({ record, type, vehicleNumberById }) {
    const tripDescription = record.description || '';
    const seatNumberStr = record.seat_number || '';
    const seatCount = record.seat_count || 0;
    const vehicle = vehicleNumberById[record.vehicle_id] || record.vehicle_number || '—';
    const bookingTime = record.booking_time ? getLocalDateAndTime(record.booking_time) : '—';
    const passenger = record.full_name || '—';
    const phone = record.phone_number || '—';

    const ticketFare = record.fare || 0;
    const gst = AFP(record.cgst || 0, record.sgst || 0);
    const total = record.ticket_cost || 0;
    const discount = record.direct_discount || 0;
    const amountPaid = record.amountPaid || 0;
    const commission = record.commission || 0;
    const tds = record.tds || 0;
    const penalty = record.cancellation_penalty || 0;
    const otaGst = (isValid(record.privilege) && record.privilege == "OTA") ? AFP(record.cgst || 0, record.sgst || 0) : 0;
    const netAmount = amountPaid - (commission - tds) - otaGst;
    const refundAmount = amountPaid - penalty;

    return (
        <div className="tx-detail-grid">
            <div className="tx-detail-section">
                <div className="l">Booking & Trip</div>
                <div className="detail-row"><span className="k">Booking time</span><span className="v">{bookingTime}</span></div>
                <div className="detail-row"><span className="k">Passenger</span><span className="v text">{passenger}</span></div>
                <div className="detail-row"><span className="k">Phone</span><span className="v">{phone}</span></div>
                <div className="detail-row"><span className="k">Vehicle</span><span className="v">{vehicle}</span></div>
                {tripDescription && <div className="detail-row"><span className="k">Trip</span><span className="v text">{tripDescription}</span></div>}
                <div className="detail-row"><span className="k">Seats ({seatCount})</span><span className="v">{seatNumberStr || '—'}</span></div>
                {record.cancelled_by && <div className="detail-row"><span className="k">Cancelled by</span><span className="v text">{record.cancelled_by}</span></div>}
            </div>
            <div className="tx-detail-section">
                <div className="l">Financial Breakdown</div>
                <div className="detail-row"><span className="k">Ticket fare (A)</span><span className="v">{fmtINRpaise(ticketFare)}</span></div>
                <div className="detail-row"><span className="k">GST (B)</span><span className="v">{fmtINRpaise(gst)}</span></div>
                <div className="detail-row"><span className="k">Total amount (A+B)</span><span className="v">{fmtINRpaise(total)}</span></div>
                {type === 'confirmed' ? (
                    <>
                        <div className="detail-row"><span className="k">Discount (D)</span><span className="v">{fmtINRpaise(discount)}</span></div>
                        <div className="detail-row"><span className="k">Amount paid (A+B−D)</span><span className="v">{fmtINRpaise(amountPaid)}</span></div>
                        <div className="detail-row"><span className="k">Commission (F)</span><span className="v">{fmtINRpaise(commission)}</span></div>
                        <div className="detail-row"><span className="k">TDS (G)</span><span className="v">{fmtINRpaise(tds)}</span></div>
                        <div className="detail-row bold"><span className="k">Net amount (Amount paid − (F − G) − OTA GST)</span><span className="v">{fmtINRpaise(netAmount)}</span></div>
                    </>
                ) : (
                    <>
                        <div className="detail-row"><span className="k">Penalty</span><span className="v">{fmtINRpaise(penalty)}</span></div>
                        <div className="detail-row bold"><span className="k">Refund to customer</span><span className="v">{fmtINRpaise(refundAmount)}</span></div>
                    </>
                )}
            </div>
        </div>
    );
}

const computeNetOrRefund = (record, isConfirmed) => {
    if (isConfirmed) {
        const otaGst = (isValid(record.privilege) && record.privilege == "OTA") ? (record.cgst || 0) + (record.sgst || 0) : 0;
        return (record.amountPaid || 0) - ((record.commission || 0) - (record.tds || 0)) - otaGst;
    }
    return (record.amountPaid || 0) - (record.cancellation_penalty || 0);
};

// ----- Display columns for the new Transactions table -----
const STATUS_LABELS = { confirmed: 'Confirmed', partial: 'Partially cancelled', cancelled: 'Cancelled' };
const STATUS_FILTER_OPTIONS = Object.keys(STATUS_LABELS).map(value => ({ text: STATUS_LABELS[value], value }));

const splitSeats = (seatNumber) => String(seatNumber || '').split(',').map(s => s.trim()).filter(Boolean);

const getStatusValues = (record) => (Array.isArray(record.travellerStatus)
    ? record.travellerStatus.filter(Boolean)
    : (record.travellerStatus ? [record.travellerStatus] : []));

// 'confirmed' | 'partial' | 'cancelled'. On the Confirmed tab a ticket whose seats also appear
// in the cancelled list (same PNR) is treated as cancelled / partially cancelled.
const getTicketStatusClass = (record, isConfirmed, cancelledSeatsByPnr) => {
    const statusValues = getStatusValues(record);
    const someCancelled = statusValues.some(s => String(s).toLowerCase().includes('cancel'));
    const someNonCancelled = statusValues.some(s => !String(s).toLowerCase().includes('cancel'));
    let cls;
    if (someCancelled && someNonCancelled) cls = 'partial';
    else if (someCancelled || (statusValues.length === 0 && !isConfirmed)) cls = 'cancelled';
    else cls = 'confirmed';

    if (isConfirmed && cls === 'confirmed' && cancelledSeatsByPnr) {
        const cancelledSeats = cancelledSeatsByPnr[record.pnr];
        const seats = splitSeats(record.seat_number);
        if (cancelledSeats && seats.length > 0) {
            const cancelledCount = seats.filter(s => cancelledSeats.has(s)).length;
            if (cancelledCount > 0) cls = cancelledCount === seats.length ? 'cancelled' : 'partial';
        }
    }
    return cls;
};

const buildDisplayColumns = ({ type, agentFilters, subAgentFilters, showSubAgent, vehicleNumberById = {}, cancelledSeatsByPnr, statusFilter }) => {
    const isConfirmed = type === 'confirmed';
    const cols = [
        {
            title: 'PNR', key: 'pnr', dataIndex: 'pnr',
            render: (text) => <span className="tx-pnr">{text || '—'}</span>,
        },
        {
            title: 'Agent', key: 'name', dataIndex: 'name',
            filters: agentFilters,
            onFilter: (value, record) => record.name === value,
            render: (text) => (
                <span className="tx-agent">
                    <span className={`dot ${getChannelClass(text)}`}></span>{text || '—'}
                </span>
            ),
        },
        ...(showSubAgent
            ? [{
                title: 'Sub Agent', key: 'sub_agent_name', dataIndex: 'sub_agent_name',
                filters: subAgentFilters,
                onFilter: (value, record) => record.sub_agent_name === value,
                render: (text) => <span className="tx-sub-agent">{text || '—'}</span>,
            }]
            : []),
        ...(isModeEnabled()
            ? [{
                title: 'Mode', key: 'mode',
                filters: [
                    { text: MODE_UPI, value: MODE_UPI },
                    { text: MODE_CASH, value: MODE_CASH },
                ],
                onFilter: (value, record) => getTicketMode(record) === value,
                render: (_t, record) => <span className="tx-mode">{getTicketMode(record)}</span>,
            }]
            : []),
        {
            title: 'Route', key: 'route',
            render: (_t, record) => (
                <div className="tx-route">
                    <div className="ot">{record.origin || '—'}<span className="arrow">→</span>{record.destination || '—'}</div>
                    {record.full_name && <div className="name">{record.full_name}</div>}
                </div>
            ),
        },
        {
            title: 'Travel', key: 'start_date',
            render: (_t, record) => {
                if (isValid(record.start_date)) {
                    return <span className="tx-date">{getLocalDate(convertDateStringToDate(record.start_date.split('T')[0]))}</span>;
                }
                return <span className="tx-date">—</span>;
            },
        },
        {
            title: 'Vehicle', key: 'vehicle_number', dataIndex: 'vehicle_id',
            render: (_t, record) => <span className="tx-date">{vehicleNumberById[record.vehicle_id] || record.vehicle_number || '—'}</span>,
        },
        {
            title: 'Seats', key: 'seat_number', dataIndex: 'seat_number',
            render: (text) => {
                const seats = String(text || '').split(',').map(s => s.trim()).filter(Boolean);
                if (seats.length === 0) return <span className="tx-seat-pill">—</span>;
                return <div className="tx-seats">{seats.map(s => <span key={s} className="tx-seat-pill">{s}</span>)}</div>;
            },
        },
        {
            title: 'Gender', key: 'gender', dataIndex: 'gender',
            filters: [
                { text: 'Male', value: 'M' },
                { text: 'Female', value: 'F' },
            ],
            onFilter: (value, record) => String(record.gender || '').includes(value),
            render: (text) => text || '—',
        },
        {
            title: 'Status', key: 'travellerStatus', dataIndex: 'travellerStatus',
            // Confirmed tab: filter options are controlled by the parent and applied in
            // TransactionsCard (no onFilter), so the tab count reflects the selection.
            ...(isConfirmed
                ? {
                    filters: STATUS_FILTER_OPTIONS,
                    filteredValue: isValidArray(statusFilter) && statusFilter.length > 0 ? statusFilter : null,
                }
                : {}),
            render: (_t, record) => {
                const statusValues = getStatusValues(record);
                const cls = getTicketStatusClass(record, isConfirmed, cancelledSeatsByPnr);
                // Show the raw status text unless seat matching changed the class (e.g. confirmed → cancelled)
                const label = cls !== 'partial' && statusValues.length > 0 && getTicketStatusClass(record, isConfirmed) === cls
                    ? statusValues.join(', ')
                    : STATUS_LABELS[cls];
                return <span className={`tx-status ${cls}`}>{label}</span>;
            },
        },
        {
            title: isConfirmed ? 'Net amount' : 'Refund',
            key: isConfirmed ? 'net_amount_display' : 'refund_amount_display',
            align: 'right',
            className: 'col-amount',
            sorter: (a, b) => computeNetOrRefund(a, isConfirmed) - computeNetOrRefund(b, isConfirmed),
            render: (_t, record) => (
                <span className="tx-amount">{fmtINR(computeNetOrRefund(record, isConfirmed))}</span>
            ),
        },
        ...(global.useRemarks === 1
            ? [{
                title: 'Remarks', key: 'remarks', dataIndex: 'remarks',
                render: (text) => <span className="tx-remarks">{text || '—'}</span>,
            }]
            : []),
    ];
    return cols;
};

function TransactionsCard({
    confirmed, cancelled, vehicleNumberById,
    statusFilter, setStatusFilter,
    confirmPaginationSize, setConfirmPaginationSize, setConfirmPage,
    cancelPaginationSize, setCancelPaginationSize, setCancelPage,
}) {
    const [tab, setTab] = useState('confirmed');
    const [search, setSearch] = useState('');
    const [sort, setSort] = useState('travel_recent');

    // Seats already cancelled, keyed by PNR — lets the Confirmed tab tell which tickets were later cancelled
    const cancelledSeatsByPnr = useMemo(() => {
        const map = {};
        (cancelled || []).forEach(t => {
            if (!t.pnr) return;
            if (!map[t.pnr]) map[t.pnr] = new Set();
            splitSeats(t.seat_number).forEach(s => map[t.pnr].add(s));
        });
        return map;
    }, [cancelled]);

    const filterAndSort = (rows, type) => {
        if (!isValidArray(rows)) return [];
        const q = search.trim().toLowerCase();
        let r = rows;
        // Status column filter (Confirmed tab) — applied here so the tab count reflects it
        if (type === 'confirmed' && isValidArray(statusFilter) && statusFilter.length > 0) {
            r = r.filter(x => statusFilter.includes(getTicketStatusClass(x, true, cancelledSeatsByPnr)));
        }
        if (q) {
            r = r.filter(x => {
                const fields = [x.pnr, x.full_name, x.name, x.phone_number, x.origin, x.destination, vehicleNumberById[x.vehicle_id]];
                return fields.some(f => f && String(f).toLowerCase().includes(q));
            });
        }
        const getNetAmount = (record) => {
            if (type === 'confirmed') {
                const otaGst = (isValid(record.privilege) && record.privilege == "OTA") ? (record.cgst || 0) + (record.sgst || 0) : 0;
                return (record.amountPaid || 0) - ((record.commission || 0) - (record.tds || 0)) - otaGst;
            }
            return (record.amountPaid || 0) - (record.cancellation_penalty || 0);
        };
        const sorted = [...r];
        switch (sort) {
            case 'travel_recent':
                sorted.sort((a, b) => String(b.start_date || '').localeCompare(String(a.start_date || ''))); break;
            case 'amount_high':
                sorted.sort((a, b) => getNetAmount(b) - getNetAmount(a)); break;
            case 'pnr':
                sorted.sort((a, b) => String(a.pnr || '').localeCompare(String(b.pnr || ''))); break;
            default: break;
        }
        return sorted;
    };

    const visibleConfirmed = filterAndSort(confirmed, 'confirmed');
    const visibleCancelled = filterAndSort(cancelled, 'cancelled');

    const data = tab === 'confirmed' ? visibleConfirmed : visibleCancelled;
    const agentFilters = useMemo(() => getAgentFilterOptions(data), [data]);
    const subAgentFilters = useMemo(() => getSubAgentFilterOptions(data), [data]);
    const showSubAgent = useMemo(
        () => [...(confirmed || []), ...(cancelled || [])].some(t => isValid(t.sub_agent_name)),
        [confirmed, cancelled]
    );
    const columns = useMemo(
        () => buildDisplayColumns({ type: tab, agentFilters, subAgentFilters, showSubAgent, vehicleNumberById, cancelledSeatsByPnr, statusFilter }),
        [tab, agentFilters, subAgentFilters, showSubAgent, vehicleNumberById, cancelledSeatsByPnr, statusFilter]
    );

    const pagination = tab === 'confirmed'
        ? getPaginationDetails(true, setConfirmPage, confirmPaginationSize, setConfirmPaginationSize)
        : getPaginationDetails(true, setCancelPage, cancelPaginationSize, setCancelPaginationSize);

    return (
        <div className="card">
            <div className="card-head">
                <div className="card-title"><span className="dot"></span>Transactions</div>
                <div className="tabs">
                    <button type="button" className={tab === 'confirmed' ? 'active' : ''} onClick={() => setTab('confirmed')}>
                        Confirmed <span className="count">{Number(visibleConfirmed.length).toLocaleString('en-IN')}</span>
                    </button>
                    <button type="button" className={tab === 'cancelled' ? 'active' : ''} onClick={() => setTab('cancelled')}>
                        Cancelled <span className="count">{Number(visibleCancelled.length).toLocaleString('en-IN')}</span>
                    </button>
                </div>
            </div>
            <div className="card-sub">
                {tab === 'confirmed'
                    ? 'Tickets with status confirmed or cancelled (cancelled tickets were first confirmed). Tap a row to see the full financial breakdown.'
                    : 'Tickets that were cancelled. Penalty and refund details available on expand.'}
            </div>

            <div className="table-toolbar">
                <div className="search">
                    {Icon.search}
                    <input
                        placeholder="Search by PNR, name, phone, city, vehicle…"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                </div>
                <div className="table-toolbar-right">
                    <span>Sort:</span>
                    <Select
                        value={sort}
                        onChange={setSort}
                        size="small"
                        style={{ width: 200 }}
                        bordered={true}
                    >
                        <Option value="travel_recent">Travel date (recent)</Option>
                        <Option value="amount_high">Highest amount</Option>
                        <Option value="pnr">PNR</Option>
                    </Select>
                </div>
            </div>

            <Table
                className="tx-table"
                rowKey={(r, idx) => `${r.pnr || ''}-${r.trip_id || ''}-${r.seat_number || ''}-${idx}`}
                columns={columns}
                dataSource={data}
                pagination={pagination}
                onChange={(_pagination, tableFilters) => {
                    if (tab === 'confirmed') setStatusFilter(tableFilters.travellerStatus || []);
                }}
                expandable={{
                    expandedRowRender: (record) => <TxDetailGrid record={record} type={tab} vehicleNumberById={vehicleNumberById} />,
                    expandIconColumnIndex: -1,
                    expandRowByClick: true,
                    rowExpandable: () => true,
                }}
                locale={{ emptyText: <div className="empty-state">No {tab} transactions match the current filters.</div> }}
                size="middle"
            />
        </div>
    );
}

// ============================================================
// Main component
// ============================================================
export const SalesTicketReport = () => {
    const [form] = Form.useForm();

    const [schedules, setSchedules] = useState(undefined);
    const [showReport, setShowReport] = useState(false);
    const [running, setRunning] = useState(false);
    const [generatedAt, setGeneratedAt] = useState(null);
    const [confirmedTickets, setConfirmedTickets] = useState(undefined);
    const [cancelledTickets, setCancelledTickets] = useState(undefined);
    const [vehicleNumberById, setVehicleNumberById] = useState({});
    const [dateType, setDateType] = useState(false);     // false = Travel, true = Booking
    const [confirmedStatusFilter, setConfirmedStatusFilter] = useState([]);   // Status column filter on the Confirmed tab
    const location = useLocation();
    // Agents requested via a deep link — applied once the sales sheet (and its agent list) loads
    const pendingAgentsRef = useRef(null);
    const [filters, setFilters] = useState({
        fromDate: undefined, toDate: undefined,
        isBookingDate: false, scheduleId: undefined, isSelectAllSchedules: true,
        agentNames: [], agentName: [], isSelectAllAgents: true,
        vehicleNumbers: [], isSelectAllVehicles: true,
        operatorNames: [], isSelectAllOperators: true,
        modes: [], isSelectAllModes: true,
    });

    const [confirmPage, setConfirmPage] = useState(1);
    const [cancelPage, setCancelPage] = useState(1);
    const [confirmPaginationSize, setConfirmPaginationSize] = useState(getDefaultPageSize);
    const [cancelPaginationSize, setCancelPaginationSize] = useState(getDefaultPageSize);

    const lastDateKeyRef = useRef('');
    const cachedTripsRef = useRef(undefined);

    // Lazy schedule fetch when date range / isBooking changes — populates schedule dropdown.
    const refetchSchedulesIfNeeded = (dateRange, isBooking) => {
        if (!dateRange || !dateRange[0] || !dateRange[1]) return;
        const fromDate = getAPIDateFormat(dateRange[0]);
        const toDate = getAPIDateFormat(dateRange[1]);
        const key = `${fromDate}|${toDate}|${!!isBooking}`;
        if (key === lastDateKeyRef.current) return;
        lastDateKeyRef.current = key;
        const fetcher = isBooking ? fetchTripsByBookingDate : fetchTripsByTravelDate;
        fetcher(fromDate, toDate, (data) => {
            setSchedules(data.schedules);
            cachedTripsRef.current = data.trips;
            getVechicles((vehicleData) => {
                setVehicleNumberById(vehicleData.reduce((acc, v) => { acc[v.id] = v.vechicle_no; return acc; }, {}));
            });
        }, () => { setSchedules([]); cachedTripsRef.current = undefined; });
    };

    // Pre-load vehicle id → number map on mount so display lookups work even before first run.
    useEffect(() => {
        getVechicles((vehicleData) => {
            setVehicleNumberById(vehicleData.reduce((acc, v) => { acc[v.id] = v.vechicle_no; return acc; }, {}));
        });
    }, []);

    const onValuesChange = (changed, all) => {
        if ('dateRange' in changed || 'isBooking' in changed) {
            refetchSchedulesIfNeeded(all.dateRange, all.isBooking);
        }
    };

    const onRunReport = (values) => {
        const { dateRange, isBooking, returnId } = values;
        const fromDate = getAPIDateFormat(dateRange[0]);
        const toDate = getAPIDateFormat(dateRange[1]);
        setShowReport(false); setRunning(true);

        const ensureTrips = () => new Promise((resolve, reject) => {
            const key = `${fromDate}|${toDate}|${!!isBooking}`;
            if (key === lastDateKeyRef.current && isValidArray(cachedTripsRef.current)) {
                return resolve(cachedTripsRef.current);
            }
            lastDateKeyRef.current = key;
            const fetcher = isBooking ? fetchTripsByBookingDate : fetchTripsByTravelDate;
            fetcher(fromDate, toDate, (data) => {
                setSchedules(data.schedules);
                cachedTripsRef.current = data.trips;
                resolve(data.trips);
            }, reject);
        });

        ensureTrips().then((tripList) => {
            const isSelectAllSchedules = !isValid(returnId);
            const tripIds = getTripIds(tripList, isSelectAllSchedules, returnId);
            if (!isValidArray(tripIds) || tripIds.length === 0) {
                setRunning(false);
                message.warning('No trips found for the selected date range.');
                return;
            }
            fetchSalesSheet(tripIds, fromDate, toDate, isBooking, (data, salesSheetFilters) => {
                const allAgents = isValidArray(salesSheetFilters?.agentNames)
                    ? [...new Set(salesSheetFilters.agentNames.map(a => (a && a.name) || a).filter(Boolean))]
                    : [];
                // Deep-linked agents: match case-insensitively against the loaded agent list
                const requestedAgents = pendingAgentsRef.current;
                pendingAgentsRef.current = null;
                const hasRequestedAgents = isValidArray(requestedAgents) && requestedAgents.length > 0;
                const selectedAgents = hasRequestedAgents
                    ? requestedAgents.map(r => allAgents.find(a => String(a).toLowerCase() === String(r).toLowerCase()) || r)
                    : allAgents;
                setFilters({
                    fromDate, toDate, isBookingDate: !!isBooking,
                    scheduleId: returnId, isSelectAllSchedules,
                    agentNames: salesSheetFilters?.agentNames || [],
                    agentName: selectedAgents,
                    isSelectAllAgents: !hasRequestedAgents,
                    vehicleNumbers: [],
                    isSelectAllVehicles: true,
                    operatorNames: [],
                    isSelectAllOperators: true,
                    modes: [],
                    isSelectAllModes: true,
                });
                setConfirmedTickets(data.confirmedTransactions);
                setCancelledTickets(data.cancelledTransactions);
                setGeneratedAt(getLocalDateAndTime(new Date()));
                setShowReport(true); setRunning(false);
            }, () => { setRunning(false); message.error('Failed to load sales sheet'); });
        }).catch(() => { setRunning(false); message.error('Failed to load trips'); });
    };

    // Deep link (e.g. from Total Transaction report): pre-fill the filters and run the report.
    useEffect(() => {
        const params = parseSalesReportLink(location.search);
        if (!params) return;
        form.setFieldsValue({ dateRange: params.dateRange, returnId: undefined });
        setDateType(params.isBookingDate);
        setConfirmedStatusFilter(params.statuses);
        form.validateFields(['dateRange']).then(() => {
            pendingAgentsRef.current = params.agents;
            onRunReport({ dateRange: params.dateRange, isBooking: params.isBookingDate, returnId: undefined });
        }).catch(() => {});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.search]);

    const onReset = () => {
        form.resetFields();
        setDateType(false);
        setConfirmedStatusFilter([]);
        setSchedules(undefined);
        cachedTripsRef.current = undefined; lastDateKeyRef.current = '';
        setConfirmedTickets(undefined); setCancelledTickets(undefined);
        setShowReport(false); setGeneratedAt(null);
        setFilters({
            fromDate: undefined, toDate: undefined,
            isBookingDate: false, scheduleId: undefined, isSelectAllSchedules: true,
            agentNames: [], agentName: [], isSelectAllAgents: true,
            vehicleNumbers: [], isSelectAllVehicles: true,
            operatorNames: [], isSelectAllOperators: true,
            modes: [], isSelectAllModes: true,
        });
    };

    // Pass `undefined` to the helper when "All vehicles" is selected — same semantics as no filter.
    const activeVehicleFilter = filters.isSelectAllVehicles ? undefined : filters.vehicleNumbers;

    // Derive filtered tickets for the SummaryCard, table, and export.
    const filteredConfirmedTickets = useMemo(
        () => getTicketsByMode(getTicketsByOperator(
            getTicketsByAgentName(confirmedTickets, filters.agentName, filters.isSelectAllAgents, activeVehicleFilter),
            filters.operatorNames, filters.isSelectAllOperators), filters.modes, filters.isSelectAllModes),
        [confirmedTickets, filters.agentName, filters.isSelectAllAgents, activeVehicleFilter, filters.operatorNames, filters.isSelectAllOperators, filters.modes, filters.isSelectAllModes]
    );
    const filteredCancelledTickets = useMemo(
        () => getTicketsByMode(getTicketsByOperator(
            getTicketsByAgentName(cancelledTickets, filters.agentName, filters.isSelectAllAgents, activeVehicleFilter),
            filters.operatorNames, filters.isSelectAllOperators), filters.modes, filters.isSelectAllModes),
        [cancelledTickets, filters.agentName, filters.isSelectAllAgents, activeVehicleFilter, filters.operatorNames, filters.isSelectAllOperators, filters.modes, filters.isSelectAllModes]
    );

    // Operator options from the loaded rows - the filter appears only when
    // bookings from more than one operator are present in the result.
    const operatorOptions = useMemo(() => {
        const names = new Set();
        [confirmedTickets, cancelledTickets].forEach((tickets) => {
            if (isValidArray(tickets)) {
                tickets.forEach((ticket) => { if (isValid(ticket.agent_operator_name)) names.add(ticket.agent_operator_name); });
            }
        });
        return Array.from(names).sort();
    }, [confirmedTickets, cancelledTickets]);

    const confirmedTicketCount = filteredConfirmedTickets.length;
    const cancelledTicketCount = filteredCancelledTickets.length;

    // Vehicle options derived from the loaded ticket data — only show vehicles that have tickets in the report.
    const vehicleOptions = useMemo(() => {
        const ids = new Set();
        const visit = (rows) => {
            if (!isValidArray(rows)) return;
            rows.forEach(t => { if (t && t.vehicle_id != null) ids.add(t.vehicle_id); });
        };
        visit(confirmedTickets); visit(cancelledTickets);
        return [...ids]
            .map(id => ({ id, label: vehicleNumberById[id] || `Vehicle ${id}` }))
            .sort((a, b) => String(a.label).localeCompare(String(b.label)));
    }, [confirmedTickets, cancelledTickets, vehicleNumberById]);

    // ============ Column factories — used for Excel export and the Printer preview ============
    const commonColumns = [
        { title: 'Origin', key: 'origin', dataIndex: 'origin', render: (text) => text },
        { title: 'Destination', key: 'destination', dataIndex: 'destination', render: (text) => text },
        { title: 'Vehicle Number', key: 'vehicle_number', dataIndex: 'vehicle_number', render: (_t, record) => vehicleNumberById[record.vehicle_id] },
        { title: 'Seat count', key: 'seat_count', dataIndex: 'seat_count', render: (text) => roundNumber(text) },
        { title: 'Ticket Fare(A)', key: 'fare', dataIndex: 'fare', render: (text) => roundNumber(text) },
        { title: 'GST(B)', key: 'gst', render: (_t, record) => AFP(record.cgst, record.sgst) },
        { title: 'Total amount', key: 'ticket_cost', dataIndex: 'ticket_cost', render: (text) => roundNumber(text) },
    ];
    const commonConfirmedTicketsColumns = [
        { title: 'Discount(D)', key: 'direct_discount', dataIndex: 'direct_discount', render: (text) => roundNumber(text) },
        {
            title: 'Amount Paid(A+B-D)', excelTitle: 'Amount paid by the customer(A-B)',
            key: 'amountPaid', dataIndex: 'amountPaid',
            render: (_t, record) => roundNumber(record.amountPaid),
        },
        { title: 'Commission(F)', key: 'commission', dataIndex: 'commission', render: (text) => roundNumber(text) },
        { title: 'TDS(G)', key: 'tds', dataIndex: 'tds', render: (text) => roundNumber(text) },
        {
            title: 'Net commission(F-G)', excelTitle: 'Net commission(Commission - TDS)',
            key: 'net_commision',
            render: (_t, record) => SFP(record.commission, record.tds),
        },
        {
            title: 'Net amount',
            excelTitle: 'Net amount(Amount Paid - Net Commission)',
            key: 'net_amount',
            render: (_t, record) => roundNumber(record.amountPaid - (record.commission - record.tds)),
        },
        { title: 'Trip description', key: 'description', dataIndex: 'description', render: (text) => text },
    ];

    const confirmTicketsColumns = [
        { title: 'SN', key: 'index', render: (_t, _r, idx) => (confirmPage - 1) * confirmPaginationSize + idx + 1 },
        {
            title: 'Travel date', key: 'travel_date',
            render: (_t, record) => isValid(record.start_date) ? getLocalDate(convertDateStringToDate(record.start_date.split('T')[0])) : '',
        },
        { title: 'PNR', key: 'pnr', dataIndex: 'pnr', render: (text) => text },
        { title: 'Agent', key: 'name', dataIndex: 'name', render: (text) => text },
        ...(isModeEnabled()
            ? [{ title: 'Mode', key: 'mode', render: (_t, record) => getTicketMode(record) }]
            : []),
        { title: 'Booking time', key: 'booking_time', render: (_t, record) => getLocalDateAndTime(record.booking_time) },
        { title: 'Seat number', key: 'seat_number', dataIndex: 'seat_number', render: (text) => text },
        { title: 'Name', key: 'full_name', dataIndex: 'full_name', render: (text) => text },
        { title: 'Gender', key: 'gender', dataIndex: 'gender', render: (text) => text || '' },
        { title: 'Phone Number', key: 'phone_number', dataIndex: 'phone_number', render: (text) => text },
        {
            title: 'Status', key: 'travellerStatus', dataIndex: 'travellerStatus',
            render: (_t, record) => Array.isArray(record.travellerStatus) ? record.travellerStatus.join(', ') : (record.travellerStatus || ''),
        },
        ...commonColumns,
        ...commonConfirmedTicketsColumns,
        { title: 'Cust GST Number', key: 'customer_gst_number', dataIndex: 'customer_gst_number', render: (text) => text },
        ...(global.useRemarks === 1
            ? [{ title: 'Remarks', key: 'remarks', dataIndex: 'remarks', render: (text) => text || '' }]
            : []),
    ];

    const commonCancelledTicketsColumns = [
        { title: 'Cancelled By', key: 'cancelled_by', dataIndex: 'cancelled_by', render: (text) => text || '' },
        { title: 'Penality', key: 'cancellation_penalty', dataIndex: 'cancellation_penalty', render: (text) => roundNumber(text) },
        { title: 'Penalty Comm(I)', key: 'penalty_commission', dataIndex: 'penalty_commission', render: (text) => roundNumber(text || 0) },
        { title: 'Commission(F)', key: 'commission', dataIndex: 'commission', render: (text) => roundNumber(text) },
        { title: 'TDS(G)', key: 'tds', dataIndex: 'tds', render: (text) => roundNumber(text) },
        {
            title: 'Net commision(F-G)', key: 'net_commision',
            render: (_t, record) => SFP(record.commission, record.tds),
        },
        {
            title: 'Net amount',
            excelTitle: 'Net amount(Amount Paid - Net Commission)',
            key: 'net_amount',
            render: (_t, record) => roundNumber(record.amountPaid - (record.commission - record.tds)),
        },
        {
            title: 'Refund amount', key: 'refund_amount',
            render: (_t, record) => {
                let notboardedTxt = "";
                if (isValidBool(record.boarded) && record.boarded == 0) notboardedTxt = ` (Not boarded)`;
                return `${roundNumber(record.amountPaid - record.cancellation_penalty)}${notboardedTxt}`;
            },
        },
        { title: 'Trip description', key: 'description', dataIndex: 'description', render: (text) => text },
    ];

    const cancelTicketsColumns = [
        { title: 'SN', key: 'index', render: (_t, _r, idx) => (cancelPage - 1) * cancelPaginationSize + idx + 1 },
        {
            title: 'Travel date', key: 'travel_date',
            render: (_t, record) => isValid(record.start_date) ? getLocalDate(convertDateStringToDate(record.start_date.split('T')[0])) : '',
        },
        { title: 'PNR', key: 'pnr', dataIndex: 'pnr', render: (text) => text },
        { title: 'Agent', key: 'name', dataIndex: 'name', render: (text) => text },
        ...(isModeEnabled()
            ? [{ title: 'Mode', key: 'mode', render: (_t, record) => getTicketMode(record) }]
            : []),
        { title: 'Booking time', key: 'booking_time', render: (_t, record) => getLocalDateAndTime(record.booking_time) },
        { title: 'Name', key: 'full_name', dataIndex: 'full_name', render: (text) => text },
        { title: 'Gender', key: 'gender', dataIndex: 'gender', render: (text) => text || '' },
        { title: 'Phone Number', key: 'phone_number', dataIndex: 'phone_number', render: (text) => text },
        ...commonColumns,
        ...commonCancelledTicketsColumns,
        ...(global.useRemarks === 1
            ? [{ title: 'Remarks', key: 'remarks', dataIndex: 'remarks', render: (text) => text || '' }]
            : []),
    ];

    const onExport = () => {
        generateJsonToExport(filters, filteredConfirmedTickets, filteredCancelledTickets, confirmTicketsColumns, cancelTicketsColumns);
    };

    // Printer's own antd button is hidden; PageHeader renders a Print button styled like Export
    const printHandlerRef = useRef(null);
    const onPrint = () => { if (printHandlerRef.current) printHandlerRef.current(); };

    const printerNode = (
        <Printer
            hideInternalButton={true}
            printHandlerRef={printHandlerRef}
            showComponent={false}
            zoomValue={0.5}
            renderComponent={() => (
                <RenderReport
                    fromDate={filters.fromDate} toDate={filters.toDate}
                    isBookingDate={filters.isBookingDate}
                    agentName={filters.agentName} isSelectAllAgents={filters.isSelectAllAgents}
                    vehicleNumber={activeVehicleFilter}
                    confirmedTickets={confirmedTickets} cancelledTickets={cancelledTickets}
                    pagination={false} vehicleNumberById={vehicleNumberById}
                    confirmTicketsColumns={confirmTicketsColumns}
                    setConfirmPage={setConfirmPage} confirmPaginationSize={confirmPaginationSize} setConfirmPaginationSize={setConfirmPaginationSize}
                    cancelTicketsColumns={cancelTicketsColumns}
                    setCancelPage={setCancelPage} cancelPaginationSize={cancelPaginationSize} setCancelPaginationSize={setCancelPaginationSize}
                />
            )}
        />
    );

    const scheduleLabel = (() => {
        if (filters.isSelectAllSchedules || !isValid(filters.scheduleId)) return 'All';
        if (isValidArray(schedules)) {
            const found = schedules.find(s => s && s.id === filters.scheduleId);
            return found?.description || 'Selected';
        }
        return 'Selected';
    })();

    return (
        <div className="report-inner-div sales-report-v2">
            <PageHeader onExport={onExport} onPrint={onPrint} canExport={showReport} printerNode={printerNode} />
            <FilterCard
                form={form}
                schedules={schedules}
                dateType={dateType}
                setDateType={(v) => { setDateType(v); refetchSchedulesIfNeeded(form.getFieldValue('dateRange'), v); }}
                onSubmit={onRunReport}
                onReset={onReset}
                onValuesChange={onValuesChange}
                running={running}
            />
            {showReport && (
                <RefineBar
                    agentOptions={filters.agentNames}
                    selectedAgents={filters.agentName}
                    isSelectAllAgents={filters.isSelectAllAgents}
                    onAgentChange={({ agentName, isSelectAllAgents }) => {
                        setFilters((f) => ({ ...f, agentName, isSelectAllAgents }));
                    }}
                    vehicleOptions={vehicleOptions}
                    selectedVehicles={filters.vehicleNumbers}
                    isSelectAllVehicles={filters.isSelectAllVehicles}
                    onVehicleChange={({ vehicleNumbers, isSelectAllVehicles }) => {
                        setFilters((f) => ({ ...f, vehicleNumbers, isSelectAllVehicles }));
                    }}
                    operatorOptions={operatorOptions}
                    selectedOperators={filters.operatorNames}
                    isSelectAllOperators={filters.isSelectAllOperators}
                    selectedModes={filters.modes}
                    isSelectAllModes={filters.isSelectAllModes}
                    onModeChange={({ modes, isSelectAllModes }) => {
                        setFilters((f) => ({ ...f, modes, isSelectAllModes }));
                    }}
                    onOperatorChange={({ operatorNames, isSelectAllOperators }) => {
                        setFilters((f) => ({ ...f, operatorNames, isSelectAllOperators }));
                    }}
                />
            )}
            <FilterSummaryChips
                filters={filters}
                generatedAt={generatedAt}
                vehicleNumberById={vehicleNumberById}
                scheduleLabel={scheduleLabel}
                hasReport={showReport}
            />
            {showReport && (
                <SummaryCard
                    filteredConfirmed={filteredConfirmedTickets}
                    filteredCancelled={filteredCancelledTickets}
                    confirmedCount={confirmedTicketCount}
                    cancelledCount={cancelledTicketCount}
                />
            )}
            {showReport && (
                <TransactionsCard
                    confirmed={filteredConfirmedTickets}
                    cancelled={filteredCancelledTickets}
                    vehicleNumberById={vehicleNumberById}
                    statusFilter={confirmedStatusFilter}
                    setStatusFilter={setConfirmedStatusFilter}
                    confirmPaginationSize={confirmPaginationSize}
                    setConfirmPaginationSize={setConfirmPaginationSize}
                    setConfirmPage={setConfirmPage}
                    cancelPaginationSize={cancelPaginationSize}
                    setCancelPaginationSize={setCancelPaginationSize}
                    setCancelPage={setCancelPage}
                />
            )}
        </div>
    );
};

// ============================================================
// Legacy RenderReport — preserved for the Printer preview (zoom 0.5).
// Renders the original layout (operator revenue table + two stacked tables).
// ============================================================
const RenderReport = ({
    fromDate, toDate, isBookingDate, agentName,
    confirmedTickets, cancelledTickets, pagination,
    isSelectAllAgents, vehicleNumber,
    confirmTicketsColumns,
    setConfirmPage, confirmPaginationSize, setConfirmPaginationSize,
    cancelTicketsColumns, setCancelPage, cancelPaginationSize, setCancelPaginationSize,
}) => {
    const filteredConfirmedTickets = getTicketsByAgentName(confirmedTickets, agentName, isSelectAllAgents, vehicleNumber);
    const filteredCancelledTickets = getTicketsByAgentName(cancelledTickets, agentName, isSelectAllAgents, vehicleNumber);

    const attachAgentFilter = (cols, rows) => (cols || []).map(col => {
        if (col.dataIndex === 'name' && col.key === 'name') {
            const agentFilters = getAgentFilterOptions(rows);
            return { ...col, filters: agentFilters, onFilter: (value, record) => record.name === value };
        }
        return col;
    });
    const confirmedColumnsWithFilters = attachAgentFilter(confirmTicketsColumns, filteredConfirmedTickets);
    const cancelledColumnsWithFilters = attachAgentFilter(cancelTicketsColumns, filteredCancelledTickets);

    const confirmedSummary = calculateSummary(filteredConfirmedTickets, confirmedColumnsWithFilters);
    const cancelledSummary = calculateSummary(filteredCancelledTickets, cancelledColumnsWithFilters);

    const renderSummaryCell = (column, summary, index) => {
        if (index === 0) return <Table.Summary.Cell key={index}><Text strong>Total</Text></Table.Summary.Cell>;
        if (column.dataIndex && summary[column.dataIndex] !== undefined) {
            return <Table.Summary.Cell key={index}><Text strong>{roundNumber(summary[column.dataIndex])}</Text></Table.Summary.Cell>;
        }
        if (column.key && summary[column.key] !== undefined) {
            return <Table.Summary.Cell key={index}><Text strong>{roundNumber(summary[column.key])}</Text></Table.Summary.Cell>;
        }
        return <Table.Summary.Cell key={index}></Table.Summary.Cell>;
    };

    return (
        <div>
            <Row justify="center" gutter={[16, 16]}><Col><Text strong>Sales report</Text></Col></Row>
            <Row gutter={[16, 0]} justify="space-between">
                <Col xs={xs} sm={sm} md={md} lg={lg} xl={xl} xxl={xxl}><Text><Text strong>From date : </Text>{fromDate}</Text></Col>
                <Col xs={xs} sm={sm} md={md} lg={lg} xl={xl} xxl={xxl}><Text><Text strong>To date : </Text>{toDate}</Text></Col>
            </Row>
            <Row gutter={[16, 0]} justify="space-between">
                <Col xs={xs} sm={sm} md={md} lg={lg} xl={xl} xxl={xxl}><Text><Text strong>Type of date : </Text>{isBookingDate ? "Booking Date" : "Travel Date"}</Text></Col>
                <Col xs={xs} sm={sm} md={md} lg={lg} xl={xl} xxl={xxl}><Text><Text strong>Generated day/time : </Text>{getLocalDateAndTime(new Date())}</Text></Col>
            </Row>
            <Row gutter={[16, 0]}>
                <Col><Text strong>Agent : {isSelectAllAgents ? "All agents" : (isValidArray(agentName) ? agentName.join(', ') : '')}</Text></Col>
            </Row>
            <Row gutter={[16, 16]} className="marginTop">
                <Col><h2>Confirmed Transactions (Includes tickets where status is either confirmed or cancelled)</h2>
                    <Table
                        columns={confirmedColumnsWithFilters}
                        dataSource={filteredConfirmedTickets}
                        pagination={getPaginationDetails(pagination, setConfirmPage, confirmPaginationSize, setConfirmPaginationSize)}
                        className="bordered-table"
                        summary={() => confirmedSummary ? (
                            <Table.Summary.Row>
                                {confirmedColumnsWithFilters.map((column, index) => renderSummaryCell(column, confirmedSummary, index))}
                            </Table.Summary.Row>
                        ) : null}
                    />
                </Col>
            </Row>
            <Row gutter={[16, 16]} className="marginTop">
                <Col><h2>Cancelled Transactions (Includes tickets where status is cancelled)</h2>
                    <Table
                        columns={cancelledColumnsWithFilters}
                        dataSource={filteredCancelledTickets}
                        pagination={getPaginationDetails(pagination, setCancelPage, cancelPaginationSize, setCancelPaginationSize)}
                        className="bordered-table"
                        summary={() => cancelledSummary ? (
                            <Table.Summary.Row>
                                {cancelledColumnsWithFilters.map((column, index) => renderSummaryCell(column, cancelledSummary, index))}
                            </Table.Summary.Row>
                        ) : null}
                    />
                </Col>
            </Row>
        </div>
    );
};
